import * as React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowBack } from "@mui/icons-material";
import {
  TextField,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { AdminPageLayout, AdminSectionCard } from "../../components/admin/AdminPageLayout";
import { AdminAction, AdminFormFooter } from "../../components/admin";
import { CreateUser } from ".";

const GENDER_OPTIONS = [
  { value: "MALE", label: "Male" },
  { value: "FEMALE", label: "Female" },
  { value: "OTHER", label: "Other" },
];

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "admin", label: "Admin" },
];

const initialForm = {
  name: "",
  mobile: "",
  email: "",
  password: "",
  city: "",
  state: "",
  district: "",
  subDistrict: "",
  village: "",
  pincode: "",
  country: "",
  dob: "",
  gender: "",
  role: "user",
};

export default function AddUserPage() {
  const navigate = useNavigate();
  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    // Clear field error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.mobile.trim()) newErrors.mobile = "Mobile number is required";
    else if (!/^\d{10,15}$/.test(form.mobile.trim()))
      newErrors.mobile = "Enter a valid mobile number (10–15 digits)";

    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      newErrors.email = "Enter a valid email address";

    if (!form.password) newErrors.password = "Password is required";
    else if (form.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    if (form.pincode && !/^\d{4,10}$/.test(form.pincode.trim()))
      newErrors.pincode = "Enter a valid pincode";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Build payload — omit empty optional fields
      const payload = {};
      Object.entries(form).forEach(([key, val]) => {
        if (val !== "") payload[key] = val;
      });

      const response = await CreateUser(payload);
      if (response.status === 201) {
        toast.success("User created successfully!");
        navigate("/users");
      } else {
        toast.error(response?.data?.message || "Failed to create user.");
      }
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: "#fafaf9",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#a8a29e",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#78716c",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#57534e",
    },
  };

  return (
    <AdminPageLayout
      eyebrow="User Management"
      title="Add New User"
      description="Fill in the details below to register a new user directly into the database."
      actions={
        <AdminAction
          variant="outline"
          startIcon={<ArrowBack fontSize="small" />}
          onClick={() => navigate("/users")}
        >
          Back to Users
        </AdminAction>
      }
    >
      <AdminSectionCard
        title="User information"
        description="All fields marked with * are required. Mobile, email and password are mandatory."
      >
        <form onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2.5} sx={{ p: 2 }}>
            {/* Full Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Rahul Sharma"
              />
            </Grid>

            {/* Mobile */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Mobile Number *"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                error={!!errors.mobile}
                helperText={errors.mobile}
                size="small"
                sx={inputSx}
                placeholder="10-digit mobile number"
                inputProps={{ maxLength: 15 }}
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email Address *"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                size="small"
                sx={inputSx}
                placeholder="user@example.com"
              />
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Password *"
                name="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password || "Minimum 6 characters"}
                size="small"
                sx={inputSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword((s) => !s)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Gender"
                name="gender"
                value={form.gender}
                onChange={handleChange}
                size="small"
                sx={inputSx}
              >
                <MenuItem value="">
                  <em>Select gender</em>
                </MenuItem>
                {GENDER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Date of Birth */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Birth"
                name="dob"
                type="date"
                value={form.dob}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                name="city"
                value={form.city}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Mumbai"
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Maharashtra"
              />
            </Grid>

            {/* District */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="District"
                name="district"
                value={form.district}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Pune"
              />
            </Grid>

            {/* Sub District */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub District"
                name="subDistrict"
                value={form.subDistrict}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Haveli"
              />
            </Grid>

            {/* Village */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Village"
                name="village"
                value={form.village}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. Manjari"
              />
            </Grid>

            {/* Pincode */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Pincode"
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                error={!!errors.pincode}
                helperText={errors.pincode}
                size="small"
                sx={inputSx}
                placeholder="e.g. 400001"
                inputProps={{ maxLength: 10 }}
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                name="country"
                value={form.country}
                onChange={handleChange}
                size="small"
                sx={inputSx}
                placeholder="e.g. India"
              />
            </Grid>

            {/* Role */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Role"
                name="role"
                value={form.role}
                onChange={handleChange}
                size="small"
                sx={inputSx}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <AdminFormFooter className="border-t border-stone-200 mt-1">
                <AdminAction
                  variant="outline"
                  onClick={() => navigate("/users")}
                  disabled={loading}
                >
                  Cancel
                </AdminAction>
                <AdminAction type="submit" loading={loading}>
                  Create User
                </AdminAction>
              </AdminFormFooter>
            </Grid>
          </Grid>
        </form>
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
